import FirestoreCondition from "../../models/interfaces/firestore-condition";
import FirestoreOrder from "../../models/interfaces/firestore-order";
import { Tag } from "../../models/interfaces/tag";
import { User } from "../../models/interfaces/user";
import FirestoreService from "./firestore-service";

const COLLECTION_NAME = "tags";

// -----------------------------------------------------------------------------------------
// #region Service Methods
// -----------------------------------------------------------------------------------------

/**
 * Add a new tag to the tags collection
 * @param {Tag} tag - The tag that is being added to the collection
 * @param {User} currentUser - The user that is logged into the application
 * @returns {Promise<Tag>} A promise to the newly added tag
 */
const add = async (tag: Tag, currentUser: User | null = null) => {
    return FirestoreService.add<Tag>(COLLECTION_NAME, tag, currentUser);
};

/**
 * Delete a tag collection by the Id
 * @param {string} id - The Id of the tag being deleted
 */
const deleteById = async (id: string) => {
    FirestoreService.deleteById(COLLECTION_NAME, id);
};

/**
 * Find the specific tag by the id
 * @param {string} id - The Id of the tag that we are retrieving
 * @returns {Promise<Tag>} A promise for the Tag we are retrieving
 */
const get = async (id: string) => {
    return FirestoreService.get<Tag>(COLLECTION_NAME, id);
};

/**
 * Find the specific user by the id
 * @param {string} id - The Id of the user that we are retrieving
 * @returns {Promise<User>} A promise for the User we are retrieving
 */
const getBy = async (conditions: FirestoreCondition[], order: FirestoreOrder[] = [], limit?: number) => {
    return FirestoreService.getBy<Tag>(COLLECTION_NAME, conditions, order, limit);
};

/**
 * Get all of the tags stored in the database
 * @returns {Promise<Tag[]>} A promise for the collection of Tags
 */
const getAll = async (order: FirestoreOrder[] = []) => {
    return FirestoreService.getAll<Tag>(COLLECTION_NAME, order);
};

/**
 * Get a snapshot of the specific tag to see when it changes
 * @param {string} id - Id of the tag document
 * @param {Function} listener - Function to listen to the changes to the document
 */
const getSnapshot = (id: string, listener: Function) => {
    return FirestoreService.getSnapshot<Tag>(COLLECTION_NAME, id, listener);
};

/**
 * Get a snapshot of the tags to see when it changes
 * @param {string} id - Id of the tag document
 * @param {Function} listener - Function to listen to the changes to the document
 */
const getSnapshotBy = (conditions: FirestoreCondition[], order: FirestoreOrder[] = [], listener: Function) => {
    return FirestoreService.getSnapshotBy<Tag>(COLLECTION_NAME, conditions, order, -1, listener);
};

/**
 * Save the specified tag in the database
 * @param {Tag} tag - The tag that is being updated
 * @param {User} currentUser - The user that is logged into the application
 * @returns {Promise<Tag>} A promise for the tag that is being updated
 */
const save = async (tag: Tag, currentUser: User | null = null) => {
    return FirestoreService.save<Tag>(COLLECTION_NAME, tag, currentUser);
};

/**
 * Update the specified tag stored in the database
 * @param {Tag} tag - The tag that is being updated
 * @param {User} currentUser - The user that is logged into the application
 * @returns {Promise<Tag>} A promise for the tag that is being updated
 */
const update = async (tag: Tag, currentUser: User | null = null) => {
    return FirestoreService.update<Tag>(COLLECTION_NAME, tag, currentUser);
};

// #endregion Service Methods


// -----------------------------------------------------------------------------------------
// #region Exports
// -----------------------------------------------------------------------------------------

const TagService = {
    add,
    deleteById,
    get,
    getBy,
    getAll,
    getSnapshot,
    getSnapshotBy,
    save,
    update,
};

export default TagService;

// #endregion Exports